import React, { useState } from 'react';
import { personalNotesApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Paperclip, Download, Trash2, Loader2 } from 'lucide-react';

const STATUS_LABELS = { da_fare: 'Da fare', in_corso: 'In corso', completata: 'Completata' };

// Crea/modifica una task personale (oggetto, descrizione, scadenza, stato, allegati).
export default function PersonalTaskDialog({ task, onClose, onSaved }) {
  const isEdit = !!task;
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [status, setStatus] = useState(task?.status || 'da_fare');
  const [attachments, setAttachments] = useState(task?.attachments || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function submit() {
    const cleaned = title.trim();
    if (!cleaned) { toast.error("L'oggetto della task è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { title: cleaned, description, due_date: dueDate || null, status };
      if (isEdit) {
        await personalNotesApi.updateTask(task.task_id, payload);
        toast.success('Task aggiornata');
      } else {
        await personalNotesApi.createTask(payload);
        toast.success('Task creata');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio task');
    } finally { setSaving(false); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !task?.task_id) {
      if (file) toast.error('Salva prima la task per poter allegare file');
      return;
    }
    setUploading(true);
    try {
      const { data } = await personalNotesApi.uploadAttachment(task.task_id, file);
      setAttachments((prev) => [...prev, data]);
      toast.success('Allegato caricato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento allegato');
    } finally { setUploading(false); }
  }

  async function handleDeleteAttachment(attId) {
    try {
      await personalNotesApi.deleteAttachment(task.task_id, attId);
      setAttachments((prev) => prev.filter((a) => a.attachment_id !== attId));
      toast.success('Allegato rimosso');
    } catch (e) {
      toast.error('Errore rimozione allegato');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="personal-task-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifica task' : 'Nuova task'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs uppercase tracking-wide">Oggetto *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Chiamare fornitore"
              autoFocus data-testid="personal-task-title-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Descrizione</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              placeholder="Dettagli aggiuntivi…" data-testid="personal-task-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Scadenza</Label>
              <Input type="date" value={dueDate || ''} onChange={(e) => setDueDate(e.target.value)}
                data-testid="personal-task-due-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Stato</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="personal-task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide">Allegati</Label>
            {!isEdit && (
              <p className="text-[11px] text-slate-500 mb-1">Salva la task per poter allegare file.</p>
            )}
            <div className="space-y-1.5 mt-1">
              {attachments.map((a) => (
                <div key={a.attachment_id} className="flex items-center justify-between gap-2 border border-slate-200 px-2 py-1.5 text-xs"
                  data-testid={`personal-task-attachment-${a.attachment_id}`}>
                  <span className="flex items-center gap-1.5 truncate min-w-0">
                    <Paperclip className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                    <span className="truncate">{a.filename}</span>
                  </span>
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <a href={personalNotesApi.attachmentUrl(task.task_id, a.attachment_id)} target="_blank"
                      rel="noreferrer" data-testid={`personal-task-attachment-download-${a.attachment_id}`}>
                      <Download className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900" />
                    </a>
                    <button onClick={() => handleDeleteAttachment(a.attachment_id)}
                      data-testid={`personal-task-attachment-delete-${a.attachment_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-slate-500 hover:text-red-600" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
            {isEdit && (
              <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 cursor-pointer border border-slate-300 px-2.5 py-1.5"
                data-testid="personal-task-attachment-upload-label">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                Aggiungi allegato
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading}
                  data-testid="personal-task-attachment-upload-input" />
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="personal-task-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={saving || !title.trim()} data-testid="personal-task-save-btn">
            {saving ? 'Salvataggio…' : (isEdit ? 'Salva' : 'Crea')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
